import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   * @param {boolean} [options.preview] - If true, show slide without updating selected index or thumbnail aria-selected (desktop hover).
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    const allSlides = this.refs.slides;
    const currentSlide = allSlides[this.current];

    for (const slide of allSlides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = allSlides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide) return;

        this.#revealHiddenSlide(requestedSlide);

        return allSlides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!allSlides.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = allSlides[requestedIndex];
    if (requestedSlideElement) {
      this.#revealHiddenSlide(requestedSlideElement);
    }

    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, allSlides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = allSlides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // Desktop hover preview: show slide without changing selected state or thumbnail aria-selected
    if (options.preview === true) {
      this.#previewSlide(index);
      return;
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = allSlides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = allSlides[index];
    if (!slide) return;

    const previousIndex = this.current;

    if (this.#usesThreeDotCycle()) {
      if (event == null && !options.preview) {
        this.#syncThreeDotCycleFromSlideIndex(index);
      } else if (event && this.#isDotButtonClick(event)) {
        const c = this.#anchorIndexToCycle(index);
        if (c !== null) {
          this.#threeDotCycleIndex = c;
          this.#armThreeDotSilence();
        }
      }
    }

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = index;
    this.#selectedIndex = index;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Desktop-only: preview a slide on thumbnail hover without changing selected state.
   * @param {number} index - The slide index to preview.
   */
  #previewSlide(index) {
    const allSlides = this.refs.slides ?? [];
    if (!allSlides.length || !this.#scroll) return;
    const slide = allSlides[index];
    if (!slide) return;
    const slidesToUpdate = allSlides;
    for (const s of slidesToUpdate) {
      s.setAttribute('aria-hidden', 'true');
    }
    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant: true });
  }

  /**
   * Preview a slide (desktop hover). Only runs when (hover: hover) and viewport ≥ 1024px.
   * Video / 3D slides are excluded so the main gallery only changes after an explicit click/tap
   * (hover preview can leave non-image media stuck visible when revert runs at the wrong time).
   * @param {number} index - The slide index to preview.
   * @param {Event} [event] - The pointer event.
   */
  preview(index, event) {
    if (!mediaQueryLarge.matches || !window.matchMedia('(hover: hover)').matches) return;
    const { slides } = this;
    const slide = slides?.[Number(index)];
    if (
      slide &&
      (slide.classList.contains('product-media-container--video') ||
        slide.classList.contains('product-media-container--external_video') ||
        slide.classList.contains('product-media-container--video-360') ||
        slide.classList.contains('product-media-container--model'))
    ) {
      return;
    }
    const animate = !prefersReducedMotion();
    this.select(Number(index), event, { animate, preview: true });
  }

  /**
   * Revert to the last click-selected slide (desktop, after pointer leave from thumbnails).
   */
  revertPreview() {
    if (!mediaQueryLarge.matches || !window.matchMedia('(hover: hover)').matches) return;
    this.select(this.#selectedIndex, undefined, { animate: false });
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    if (this.#usesThreeDotCycle()) {
      this.#stepThreeDotCycle('forward');
      this.#armThreeDotSilence();
    }
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    if (this.#usesThreeDotCycle()) {
      this.#stepThreeDotCycle('backward');
      this.#armThreeDotSilence();
    }
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  /**
   * Last click-selected slide index; used to revert after thumbnail hover preview (desktop).
   * @type {number}
   */
  #selectedIndex = 0;

  /**
   * When pagination shows 3 dots but there are more slides, which dot is active (0–2).
   * Advances one step per arrow click, swipe snap, or drag end — not derived from slide index alone.
   * @type {number}
   */
  #threeDotCycleIndex = 0;

  /**
   * While performance.now() < this value, scroll-driven sync must not bump {@link #threeDotCycleIndex}
   * (avoids double-counting after arrows, dots, or drag which already updated the cycle).
   * @type {number}
   */
  #threeDotSilenceScrollCycleUntil = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      if (!controls) continue;
      const selectedIndex =
        controls === dots && this.#usesThreeDotCycle() ? this.#threeDotCycleIndex : value;
      controls.forEach((el, i) => {
        // Theme setting "Thumbnails say which image is shown with aria-current" (on by default):
        // snippets/slideshow-controls.liquid marks those thumbnails data-aria-current. A button may
        // not carry aria-selected; aria-current="true" on the shown one says the same to screen readers.
        if (el.hasAttribute('data-aria-current')) {
          if (i === selectedIndex) el.setAttribute('aria-current', 'true');
          else el.removeAttribute('aria-current');
          return;
        }
        el.setAttribute('aria-selected', `${i === selectedIndex}`);
      });
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * @returns {boolean} True when Liquid renders 3 dot buttons but there are more slides than dots.
   */
  #usesThreeDotCycle() {
    const { dots, slides } = this.refs;
    return (dots?.length ?? 0) === 3 && (slides?.length ?? 0) > 3;
  }

  /** Ignore scroll-driven dot cycle bumps until this deadline (performance.now()). */
  #armThreeDotSilence(durationMs = 450) {
    this.#threeDotSilenceScrollCycleUntil = performance.now() + durationMs;
  }

  /**
   * Advance the 3-dot cycle one step (wraps 0–2).
   * @param {'forward' | 'backward'} direction
   */
  #stepThreeDotCycle(direction) {
    if (direction === 'forward') {
      this.#threeDotCycleIndex = (this.#threeDotCycleIndex + 1) % 3;
    } else {
      this.#threeDotCycleIndex = (this.#threeDotCycleIndex + 2) % 3;
    }
  }

  /**
   * Dot buttons map to first / middle / last slide (see slideshow-controls.liquid).
   * @param {number} slideIndex
   * @returns {number | null} Dot index 0–2, or null if not an anchor slide.
   */
  #anchorIndexToCycle(slideIndex) {
    const { slides } = this.refs;
    if (!slides?.length) return null;
    const last = slides.length - 1;
    const mid = Math.floor(last / 2);
    if (slideIndex === 0) return 0;
    if (slideIndex === last) return 2;
    if (slideIndex === mid) return 1;
    return null;
  }

  /**
   * Align dot cycle with slide for programmatic updates (no user event).
   * @param {number} slideIndex
   */
  #syncThreeDotCycleFromSlideIndex(slideIndex) {
    if (!this.#usesThreeDotCycle()) return;
    const { slides } = this.refs;
    if (!slides?.length) return;
    const last = slides.length - 1;
    if (last <= 0) {
      this.#threeDotCycleIndex = 0;
      return;
    }
    this.#threeDotCycleIndex = Math.min(2, Math.round((slideIndex / last) * 2));
  }

  /**
   * @param {number} from
   * @param {number} to
   * @returns {'forward' | 'backward'}
   */
  #threeDotScrollDirection(from, to) {
    const { slides } = this.refs;
    const len = slides?.length ?? 0;
    if (len < 2) return 'forward';
    const last = len - 1;
    if (this.infinite) {
      if (from === last && to === 0) return 'forward';
      if (from === 0 && to === last) return 'backward';
    }
    if (to > from) return 'forward';
    if (to < from) return 'backward';
    return 'forward';
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isDotButtonClick(event) {
    const target = event?.target;
    if (!(target instanceof Element)) return false;
    const dots = this.refs.dots;
    if (!dots?.length) return false;
    return [...dots].some((btn) => btn === target || btn.contains(target));
  }

  /**
   * Lazy-hidden slides (PDP perf, variant card galleries) use `hidden` + `reveal` so they stay
   * out of layout until selected; numeric thumbnail/arrow navigation must reveal before scroll.
   *
   * @param {Element} slide
   */
  #revealHiddenSlide(slide) {
    if (!slide?.hasAttribute('hidden')) return;
    slide.setAttribute('reveal', '');
    slide.setAttribute('aria-hidden', 'false');
  }

  /**
   * Reads which slide is closest to the scroller focus; does not mutate state.
   * @returns {number}
   */
  #computeSyncIndex() {
    const { slides } = this;
    if (!slides) return 0;

    if (!this.#scroll) return 0;

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.#current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint =
      visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return 0;

    return slides.indexOf(closestVisibleSlide);
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();
    // Theme setting "Thumbnail arrows checked after layout" (on by default; snippets/slideshow-controls.liquid
    // prints thumbnail-arrows-after-layout): the thumbnail arrow check runs only in the ResizeObserver set up
    // below, whose first call comes after the browser's own layout of the frame, before it paints. Here and in
    // the frame callback below it would read sizes while styles are still pending and force a recalculation.
    if (!this.#thumbnailArrowsAfterLayout) this.#updateThumbnailArrowVisibility();

    this.disabled = this.isNested || this.disabled;

    // ANIM-HOLD (theme setting "Animations start after the page has loaded", on by default):
    // while snippets/perf-anim-hold.liquid holds first-screen animations, autoplay starts at its
    // release (load, or 10 s at most). The autoplay timer skips its ticks while hovered, as today.
    if (document.documentElement.hasAttribute('data-amg-anim-hold')) {
      document.addEventListener('amg:anim-release', () => this.isConnected && this.resume(), { once: true });
    } else {
      this.resume();
    }

    if (this.#usesThreeDotCycle()) {
      this.#syncThreeDotCycleFromSlideIndex(this.initialSlideIndex);
    }
    this.current = this.initialSlideIndex;
    this.#selectedIndex = this.current;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }

        if (!this.#thumbnailArrowsAfterLayout) this.#updateThumbnailArrowVisibility();
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        this.#updateThumbnailArrowVisibility();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
      if (this.refs.slideshowControls instanceof HTMLElement) {
        this.#resizeObserver.observe(this.refs.slideshowControls);
      }
      if (this.refs.thumbnailsContainer instanceof HTMLElement) {
        this.#resizeObserver.observe(this.refs.thumbnailsContainer);
      }
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#computeSyncIndex();

    if (index === previousIndex) return;

    if (
      this.#usesThreeDotCycle() &&
      performance.now() >= this.#threeDotSilenceScrollCycleUntil
    ) {
      this.#stepThreeDotCycle(this.#threeDotScrollDirection(previousIndex, index));
    }

    this.current = index;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const index = this.#computeSyncIndex();
    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current: startSlideIndex, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier =
        startSlideIndex !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      if (this.#usesThreeDotCycle() && newIndex !== startSlideIndex) {
        this.#stepThreeDotCycle(this.#threeDotScrollDirection(startSlideIndex, newIndex));
        this.#armThreeDotSilence();
      }

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  #updateThumbnailArrowVisibility() {
    const { slideshowControls, thumbnailsContainer } = this.refs;

    if (!(slideshowControls instanceof HTMLElement) || !(thumbnailsContainer instanceof HTMLElement)) return;
    if (!slideshowControls.hasAttribute('thumbnails')) return;

    const thumbnailsWithArrows = slideshowControls.querySelector('.slideshow-controls__thumbnails-with-arrows');
    const thumbnailsList = thumbnailsContainer.querySelector('.slideshow-controls__thumbnails');
    const isVertical = window.getComputedStyle(thumbnailsWithArrows ?? thumbnailsContainer).flexDirection === 'column';

    if (!(thumbnailsList instanceof HTMLElement)) return;

    const availableSize = isVertical ? slideshowControls.clientHeight : slideshowControls.clientWidth;
    if (availableSize <= 0) return;

    const overflows = isVertical
      ? thumbnailsList.scrollHeight > availableSize + 1
      : thumbnailsList.scrollWidth > availableSize + 1;

    slideshowControls.toggleAttribute('thumbnail-arrows-needed', overflows);
  }

  /** True when the thumbnail arrow check waits for the ResizeObserver (theme setting, see #setupSlideshow). */
  get #thumbnailArrowsAfterLayout() {
    const { slideshowControls } = this.refs;
    return slideshowControls instanceof HTMLElement && slideshowControls.hasAttribute('thumbnail-arrows-after-layout');
  }

  /** Scroll amount for thumbnail strip arrows (px). */
  static THUMBNAILS_SCROLL_DELTA = 200;

  /**
   * Scrolls the thumbnail strip backward (left or up).
   */
  scrollThumbnailsPrev() {
    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
    const delta = -Slideshow.THUMBNAILS_SCROLL_DELTA;
    if (thumbnailsContainer.scrollWidth > thumbnailsContainer.clientWidth) {
      thumbnailsContainer.scrollBy({ left: delta, behavior: 'smooth' });
    } else {
      thumbnailsContainer.scrollBy({ top: delta, behavior: 'smooth' });
    }
  }

  /**
   * Scrolls the thumbnail strip forward (right or down).
   */
  scrollThumbnailsNext() {
    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
    const delta = Slideshow.THUMBNAILS_SCROLL_DELTA;
    if (thumbnailsContainer.scrollWidth > thumbnailsContainer.clientWidth) {
      thumbnailsContainer.scrollBy({ left: delta, behavior: 'smooth' });
    } else {
      thumbnailsContainer.scrollBy({ top: delta, behavior: 'smooth' });
    }
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
